import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import LuxuryButton from '../../components/ui/LuxuryButton';
import LuxuryInput from '../../components/ui/LuxuryInput';
import { UserService } from '../../api/user.api';

const AccountAddresses = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States',
        label: 'home',
        isDefault: false
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        loadAddresses();

        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const loadAddresses = async () => {
        try {
            setLoading(true);
            const data = await UserService.getAddresses();
            setAddresses(data);
        } catch (err) {
            setError(err.message || 'Failed to load addresses');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setError(null);

            if (editingId) {
                await UserService.updateAddress(editingId, formData);
                setSuccess('Address updated successfully!');
            } else {
                await UserService.addAddress(formData);
                setSuccess('Address added successfully!');
            }

            setShowAddForm(false);
            setEditingId(null);
            resetForm();
            await loadAddresses();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to save address');
        }
    };

    const handleEdit = (address) => {
        setFormData({
            name: address.name,
            email: address.email,
            phone: address.phone,
            address: address.address,
            city: address.city,
            state: address.state,
            zipCode: address.zipCode,
            country: address.country || 'United States',
            label: address.label || 'home',
            isDefault: address.isDefault || false
        });
        setEditingId(address._id);
        setShowAddForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (addressId) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;

        try {
            await UserService.deleteAddress(addressId);
            setSuccess('Address deleted successfully!');
            await loadAddresses();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to delete address');
        }
    };

    const handleSetDefault = async (addressId) => {
        try {
            await UserService.setDefaultAddress(addressId);
            setSuccess('Default address updated!');
            await loadAddresses();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to set default address');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'United States',
            label: 'home',
            isDefault: false
        });
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingId(null);
        resetForm();
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 overflow-x-hidden">
            {/* Fixed Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Page Content */}
            <motion.main
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="pt-28 pb-16"
            >
                <div className="max-w-6xl mx-auto px-4">
                    {/* Notifications */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="mb-6 p-4 bg-error/10 border border-error/20 rounded-2xl flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-error/20 flex items-center justify-center">
                                        <FaTimes className="w-4 h-4 text-error" />
                                    </div>
                                    <p className="text-error text-sm">{error}</p>
                                </div>
                                <button
                                    onClick={() => setError(null)}
                                    className="text-charcoal/50 hover:text-charcoal"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        )}

                        {success && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="mb-6 p-4 bg-sage/10 border border-sage/20 rounded-2xl flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center">
                                        <FaCheck className="w-4 h-4 text-sage" />
                                    </div>
                                    <p className="text-sage text-sm">{success}</p>
                                </div>
                                <button
                                    onClick={() => setSuccess(null)}
                                    className="text-charcoal/50 hover:text-charcoal"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Hero */}
                    <AccountHeroBit
                        title="Address Book"
                        subtitle="Manage your delivery addresses"
                        background="sage"
                    />

                    {/* Add/Edit Form */}
                    <AnimatePresence>
                        {showAddForm && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mb-8 luxury-card overflow-hidden"
                            >
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-xl font-medium text-charcoal">
                                            {editingId ? 'Edit Address' : 'Add New Address'}
                                        </h3>
                                        <button
                                            onClick={handleCancel}
                                            className="text-charcoal/50 hover:text-charcoal"
                                        >
                                            <FaTimes className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <LuxuryInput
                                                label="Full Name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <LuxuryInput
                                                type="email"
                                                label="Email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <LuxuryInput
                                                type="tel"
                                                label="Phone Number"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <div>
                                                <label className="block text-sm text-charcoal/50 mb-2">
                                                    Address Label
                                                </label>
                                                <select
                                                    name="label"
                                                    value={formData.label}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 rounded-xl border border-nude bg-white focus:border-champagne focus:ring-2 focus:ring-champagne/20 outline-none transition-all"
                                                >
                                                    <option value="home">Home</option>
                                                    <option value="work">Work</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>

                                            <div className="md:col-span-2">
                                                <LuxuryInput
                                                    label="Street Address"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>

                                            <LuxuryInput
                                                label="City"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <LuxuryInput
                                                label="State/Province"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <LuxuryInput
                                                label="ZIP/Postal Code"
                                                name="zipCode"
                                                value={formData.zipCode}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <LuxuryInput
                                                label="Country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                required
                                            />

                                            <div className="md:col-span-2">
                                                <label className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        name="isDefault"
                                                        checked={formData.isDefault}
                                                        onChange={handleInputChange}
                                                        className="w-5 h-5 text-champagne rounded focus:ring-champagne/20"
                                                    />
                                                    <span className="text-charcoal">Set as default address</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 pt-4">
                                            <LuxuryButton
                                                variant="secondary"
                                                onClick={handleCancel}
                                                icon={FaTimes}
                                            >
                                                Cancel
                                            </LuxuryButton>
                                            <LuxuryButton
                                                type="submit"
                                                icon={FaCheck}
                                            >
                                                {editingId ? 'Update Address' : 'Save Address'}
                                            </LuxuryButton>
                                        </div>
                                    </form>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                        <h2 className="text-2xl font-medium text-charcoal">
                            Saved Addresses {addresses.length > 0 && `(${addresses.length})`}
                        </h2>

                        {!showAddForm && (
                            <LuxuryButton
                                onClick={() => setShowAddForm(true)}
                                icon={<FaPlus />}
                                size="medium"
                            >
                                Add New Address
                            </LuxuryButton>
                        )}
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="grid md:grid-cols-2 gap-6">
                            {[1, 2].map(i => (
                                <div key={i} className="luxury-card p-6 animate-pulse">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-full bg-nude/20"></div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-nude/20 rounded w-32"></div>
                                            <div className="h-3 bg-nude/20 rounded w-48"></div>
                                            <div className="h-3 bg-nude/20 rounded w-40"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : addresses.length > 0 ? (
                        /* Address List */
                        <div className="grid md:grid-cols-2 gap-6">
                            {addresses.map((addr) => (
                                <motion.div
                                    key={addr._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    whileHover={{ y: -4 }}
                                    transition={{ duration: 0.3 }}
                                    className={`p-6 rounded-2xl border bg-white shadow-subtle transition-all ${
                                        addr.isDefault
                                            ? 'border-champagne ring-1 ring-champagne/30'
                                            : 'border-nude hover:border-champagne'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-nude/30 flex items-center justify-center">
                                                <FaMapMarkerAlt className="text-charcoal/50" />
                                            </div>

                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-charcoal">
                                                        {addr.name}
                                                    </span>
                                                    {addr.isDefault && (
                                                        <span className="text-xs px-2 py-1 bg-champagne/20 text-champagne rounded-full">
                                                            Default
                                                        </span>
                                                    )}
                                                    <span className="text-xs px-2 py-1 bg-nude/20 text-charcoal/50 rounded-full">
                                                        {addr.label}
                                                    </span>
                                                </div>

                                                <p className="text-sm text-charcoal/60">
                                                    {addr.address}
                                                </p>
                                                <p className="text-sm text-charcoal/60">
                                                    {addr.city}, {addr.state} {addr.zipCode}
                                                </p>
                                                <p className="text-sm text-charcoal/60">
                                                    {addr.country}
                                                </p>
                                                <p className="text-sm text-charcoal/50 mt-1">
                                                    {addr.phone}
                                                </p>
                                                <p className="text-sm text-charcoal/50">
                                                    {addr.email}
                                                </p>
                                            </div>
                                        </div>

                                        {!addr.isDefault && (
                                            <button
                                                onClick={() => handleSetDefault(addr._id)}
                                                className="text-xs text-champagne hover:underline"
                                            >
                                                Set default
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex gap-3 mt-6">
                                        <LuxuryButton
                                            size="small"
                                            variant="secondary"
                                            icon={<FaEdit />}
                                            onClick={() => handleEdit(addr)}
                                        >
                                            Edit
                                        </LuxuryButton>

                                        <LuxuryButton
                                            size="small"
                                            variant="ghost"
                                            icon={<FaTrash />}
                                            onClick={() => handleDelete(addr._id)}
                                        >
                                            Remove
                                        </LuxuryButton>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        /* Empty State */
                        <div className="text-center py-20 luxury-card">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-nude/30 flex items-center justify-center">
                                <FaMapMarkerAlt className="w-12 h-12 text-charcoal/30" />
                            </div>
                            <h3 className="text-2xl font-medium text-charcoal mb-3">
                                No addresses saved
                            </h3>
                            <p className="text-charcoal/60 mb-8 max-w-md mx-auto">
                                Add your first delivery address to make checkout faster and easier.
                            </p>
                            <LuxuryButton
                                onClick={() => setShowAddForm(true)}
                                icon={<FaPlus />}
                                size="large"
                            >
                                Add Your First Address
                            </LuxuryButton>
                        </div>
                    )}
                </div>
            </motion.main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default AccountAddresses;